import type { Partner } from '../types.ts';
import { formatAddress } from '../types.ts';

/**
 * Accessible info panel rendered inside the host's shadow root.
 */
export class InfoPanel {
  #host: ShadowRoot;
  #el: HTMLElement;
  #backdrop: HTMLElement;
  #lastFocus: Element | null = null;
  #onKeydown: (e: KeyboardEvent) => void;

  constructor(host: ShadowRoot) {
    this.#host = host;

    this.#backdrop = document.createElement('div');
    this.#backdrop.className = 'panel-backdrop';
    this.#backdrop.hidden = true;
    this.#backdrop.addEventListener('click', () => this.close());

    this.#el = document.createElement('aside');
    this.#el.className = 'panel';
    this.#el.setAttribute('role', 'dialog');
    this.#el.setAttribute('aria-modal', 'true');
    this.#el.setAttribute('aria-labelledby', 'panel-title');
    this.#el.hidden = true;

    this.#onKeydown = (e) => {
      if (e.key === 'Escape' && !this.#el.hidden) {
        e.stopPropagation();
        this.close();
      }
    };
  }

  mount(container: HTMLElement) {
    container.appendChild(this.#backdrop);
    container.appendChild(this.#el);
  }

  open(partner: Partner) {
    this.#lastFocus = (this.#host.activeElement ?? document.activeElement) as Element | null;
    this.#el.innerHTML = this.#template(partner);
    this.#el.hidden = false;
    this.#backdrop.hidden = false;
    // Force the browser to commit the closed start state (translateX(100%) / opacity:0)
    // before flipping to open, so the ENTER transition actually animates instead of
    // snapping. The element just left display:none (hidden), where a single rAF is
    // unreliable for capturing the transition's starting value.
    void this.#el.offsetWidth; // reflow
    this.#el.classList.add('panel--open');
    this.#backdrop.classList.add('panel-backdrop--open');
    document.addEventListener('keydown', this.#onKeydown, true);
    const closeBtn = this.#el.querySelector<HTMLButtonElement>('[data-action="close"]');
    // preventScroll: the panel is still at translateX(100%) (off-screen, clipped by the
    // overflow:hidden root) when this runs, so a plain focus() would scroll the page to
    // reveal it — which looks like the map "dragging" on open. Keep focus for a11y, no scroll.
    closeBtn?.focus({ preventScroll: true });
    closeBtn?.addEventListener('click', () => this.close());
  }

  close() {
    if (this.#el.hidden) return;
    this.#el.classList.remove('panel--open');
    this.#backdrop.classList.remove('panel-backdrop--open');
    document.removeEventListener('keydown', this.#onKeydown, true);
    const onEnd = () => {
      this.#el.hidden = true;
      this.#backdrop.hidden = true;
      this.#el.removeEventListener('transitionend', onEnd);
    };
    this.#el.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 350);
    (this.#lastFocus as HTMLElement | null)?.focus?.({ preventScroll: true });
  }

  destroy() {
    document.removeEventListener('keydown', this.#onKeydown, true);
    this.#el.remove();
    this.#backdrop.remove();
  }

  #template(p: Partner): string {
    const website = p.website
      ? `<a class="panel__link" href="${this.#esc(p.website)}" target="_blank" rel="noopener noreferrer">
           Webseite besuchen <span aria-hidden="true">↗</span>
           <span class="visually-hidden"> (öffnet in neuem Tab)</span>
         </a>`
      : '';
    const phone = p.phone
      ? `<a class="panel__link" href="tel:${this.#esc(p.phone.replace(/\s/g, ''))}">${this.#esc(p.phone)}</a>`
      : '';
    const email = p.email
      ? `<a class="panel__link" href="mailto:${this.#esc(p.email)}">${this.#esc(p.email)}</a>`
      : '';
    return `
      <header class="panel__header">
        <h3 id="panel-title" class="panel__title">${this.#esc(p.name)}</h3>
        <button type="button" class="panel__close" data-action="close" aria-label="Schließen">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" fill="none"/>
          </svg>
        </button>
      </header>
      <div class="panel__body">
        <p class="panel__address">${this.#esc(formatAddress(p))}<br>${this.#esc(p.address.country)}</p>
        <dl class="panel__contact">
          ${website ? `<dt>Web</dt><dd>${website}</dd>` : ''}
          ${phone ? `<dt>Telefon</dt><dd>${phone}</dd>` : ''}
          ${email ? `<dt>E-Mail</dt><dd>${email}</dd>` : ''}
        </dl>
      </div>
    `;
  }

  #esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => {
      switch (c) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return c;
      }
    });
  }
}
